import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Monitor, Smartphone, UserCog, ListPlus, Users, Lock, CalendarCheck2, FileCheck2, Contact, FileSignature, Stamp, BellRing } from "lucide-react";
import { AttendanceRulesForm } from "../../components/attendance/Rules";   // Phase 168
import { SuperAdminSafPanel } from "../../components/saf/SuperAdminSafPanel";   // Phase 210
import { SuperAdminCitizensPanel } from "../../components/citizens/SuperAdminCitizensPanel";   // Phase 215
import { SuperAdminPoliciesPanel } from "../../components/policies/SuperAdminPoliciesPanel";   // Phase 218
import { SuperAdminWatermarkPanel } from "../../components/watermark/SuperAdminWatermarkPanel";   // Phase 224
import { SuperAdminMessagesPanel } from "../../components/messages/SuperAdminMessagesPanel";   // Phase 227
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg } from "../../lib/api";

const LEVELS = [
  { value: "none", label: "No access" },
  { value: "view", label: "View only" },
  { value: "edit", label: "View & edit" },
  { value: "write", label: "Full access" },
];
const LEVEL_BADGE = {
  view: "bg-amber-100 text-amber-800 border-amber-200",
  edit: "bg-sky-100 text-sky-800 border-sky-200",
  write: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const PORTAL_META = {
  municipal_admin: { label: "Admin Console", device: "Desktop", icon: Monitor },
  supervisor: { label: "Supervisor Console", device: "Desktop", icon: Monitor },
  tax_collector: { label: "Field Agent App", device: "Mobile", icon: Smartphone },
};
const FIELD_TYPE_LABELS = { text: "Text", number: "Number", date: "Date", select: "Dropdown" };

// ---------- Custom Role add/edit dialog ----------
function RoleDialog({ code, open, onOpenChange, registry, baseRoles, role, onSaved }) {
  const isEdit = !!role;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [baseRole, setBaseRole] = useState("supervisor");
  const [modules, setModules] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(role?.name || "");
      setDescription(role?.description || "");
      setBaseRole(role?.base_role || "supervisor");
      setModules(role?.modules || {});
    }
  }, [open, role]);

  const regs = registry?.[baseRole] || [];
  const grantedCount = regs.filter((m) => m.always_on || (modules[m.key] && modules[m.key] !== "none")).length;
  const baseLocked = isEdit && (role?.user_count || 0) > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Role name is required");
    setBusy(true);
    try {
      const body = { name: name.trim(), description: description.trim(), base_role: baseRole, modules };
      if (isEdit) {
        await api.put(`/platform/municipalities/${code}/config/roles/${role.id}`, body);
        toast.success(`Role "${name.trim()}" updated — access changes apply to assigned staff immediately`);
      } else {
        await api.post(`/platform/municipalities/${code}/config/roles`, body);
        toast.success(`Custom role "${name.trim()}" created`);
      }
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserCog className="h-4 w-4" /> {isEdit ? `Edit role — ${role?.name}` : "New custom role"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Role name <span className="text-destructive">*</span></Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., SWM Inspector"
                required data-testid="role-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this role do?" data-testid="role-description-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="flex items-center gap-1.5">
              Portal template <span className="text-destructive">*</span>
              {baseLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
            </Label>
            <Select value={baseRole} onValueChange={(v) => { setBaseRole(v); setModules({}); }} disabled={baseLocked}>
              <SelectTrigger data-testid="role-base-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(baseRoles || []).map((b) => {
                  const meta = PORTAL_META[b.key];
                  return (
                    <SelectItem key={b.key} value={b.key}>
                      {meta?.label} ({meta?.device})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              {baseLocked
                ? "Portal cannot change while staff hold this role — reassign them first."
                : baseRole === "tax_collector"
                  ? "Staff with this role sign into the mobile field app (offline-capable)."
                  : baseRole === "supervisor"
                    ? "Staff with this role sign into the desktop supervisor console."
                    : "Staff with this role sign into the desktop admin console."}
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Menu / module access</Label>
              <span className="text-xs text-muted-foreground" data-testid="role-granted-count">
                {grantedCount} of {regs.length} modules granted
              </span>
            </div>
            <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
              {regs.map((m) => (
                <div key={m.key} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="text-sm min-w-0 truncate">{m.label}</span>
                  {m.always_on ? (
                    <Badge variant="outline" className="text-[10px] shrink-0">Always on</Badge>
                  ) : (
                    <Select value={modules[m.key] || "none"}
                      onValueChange={(v) => setModules({ ...modules, [m.key]: v })}>
                      <SelectTrigger className="h-8 w-36 shrink-0" data-testid={`role-module-${m.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.filter((l) => !m.levels || m.levels.includes(l.value)).map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Modules left as "No access" stay hidden from this role's menu and their APIs are blocked.
            </p>
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="role-save-button">
            {busy ? "Saving..." : isEdit ? "Save role" : "Create role"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Custom Field add/edit dialog ----------
function FieldDialog({ code, open, onOpenChange, field, onSaved }) {
  const isEdit = !!field;
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [optionsText, setOptionsText] = useState("");
  const [required, setRequired] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel(field?.label || "");
      setFieldType(field?.field_type || "text");
      setOptionsText((field?.options || []).join(", "));
      setRequired(!!field?.required);
    }
  }, [open, field]);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return toast.error("Field label is required");
    const options = optionsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (fieldType === "select" && options.length === 0) return toast.error("Dropdown fields need at least one option");
    setBusy(true);
    try {
      const body = { label: label.trim(), field_type: fieldType, options, required };
      if (isEdit) {
        await api.put(`/platform/municipalities/${code}/config/fields/${field.id}`, body);
        toast.success(`Field "${label.trim()}" updated`);
      } else {
        await api.post(`/platform/municipalities/${code}/config/fields`, body);
        toast.success(`Field "${label.trim()}" added to the staff form`);
      }
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="field-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ListPlus className="h-4 w-4" /> {isEdit ? `Edit field — ${field?.label}` : "New custom field"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label>Field label <span className="text-destructive">*</span></Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Aadhaar Number" required data-testid="field-label-input" />
          </div>
          <div className="space-y-1">
            <Label>Field type</Label>
            <Select value={fieldType} onValueChange={setFieldType}>
              <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="select">Dropdown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {fieldType === "select" && (
            <div className="space-y-1">
              <Label>Options (comma separated)</Label>
              <Input value={optionsText} onChange={(e) => setOptionsText(e.target.value)}
                placeholder="e.g., Morning, Evening, Night" data-testid="field-options-input" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={required} onCheckedChange={setRequired} data-testid="field-required-switch" />
            Required when creating staff
          </label>
          <Button type="submit" className="w-full" disabled={busy} data-testid="field-save-button">
            {busy ? "Saving..." : isEdit ? "Save field" : "Add field"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TenantConfig() {
  const [munis, setMunis] = useState(null);
  const [code, setCode] = useState("");
  const [rolesData, setRolesData] = useState(null);
  const [fieldsData, setFieldsData] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [deleteRole, setDeleteRole] = useState(null);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editField, setEditField] = useState(null);
  const [deleteField, setDeleteField] = useState(null);

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data.items || [];
        setMunis(list);
        if (list.length > 0) setCode(list[0].code);
      })
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);

  const loadRoles = useCallback(() => {
    if (!code) return;
    api.get(`/platform/municipalities/${code}/config/roles`)
      .then((r) => setRolesData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setRolesData({ custom_roles: [], registry: {}, base_roles: [] }); });
  }, [code]);

  const loadFields = useCallback(() => {
    if (!code) return;
    api.get(`/platform/municipalities/${code}/config/fields`)
      .then((r) => setFieldsData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setFieldsData({ custom_fields: [] }); });
  }, [code]);

  useEffect(() => { setRolesData(null); setFieldsData(null); loadRoles(); loadFields(); }, [loadRoles, loadFields]);

  const confirmDeleteRole = async () => {
    try {
      await api.delete(`/platform/municipalities/${code}/config/roles/${deleteRole.id}`);
      toast.success(`Role "${deleteRole.name}" deleted`);
      setDeleteRole(null);
      loadRoles();
    } catch (err) { toast.error(errMsg(err)); setDeleteRole(null); }
  };

  const confirmDeleteField = async () => {
    try {
      await api.delete(`/platform/municipalities/${code}/config/fields/${deleteField.id}`);
      toast.success(`Field "${deleteField.label}" removed from the staff form`);
      setDeleteField(null);
      loadFields();
    } catch (err) { toast.error(errMsg(err)); setDeleteField(null); }
  };

  if (munis === null) return <Spinner />;

  const roles = rolesData?.custom_roles || [];
  const fields = fieldsData?.custom_fields || [];

  return (
    <div>
      <PageHeader title="Tenant Configuration"
        subtitle="Per-municipality custom staff roles (with menu access), custom staff-form fields, tax-collector attendance rules, citizen self-assessments, citizen-module permissions, company policies, the logo watermark on notices & receipts, and message delivery (citizen / staff switches, automatic vs manual sending)" />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Label className="text-sm shrink-0">Municipality</Label>
        <Select value={code} onValueChange={setCode}>
          <SelectTrigger className="w-72" data-testid="tenantconfig-muni-select"><SelectValue placeholder="Select municipality" /></SelectTrigger>
          <SelectContent>
            {munis.map((m) => <SelectItem key={m.code} value={m.code}>{m.name} ({m.code})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!code ? (
        <EmptyState title="No municipality selected" subtitle="Create a municipality first, then configure its roles and fields." />
      ) : (
        <Tabs defaultValue={["roles", "fields", "attendance", "saf", "citizens", "policies", "watermark", "messages"].includes(window.location.hash.replace("#", "")) ? window.location.hash.replace("#", "") : "roles"}>
          <TabsList data-testid="tenantconfig-tabs">
            <TabsTrigger value="roles" data-testid="tab-custom-roles">
              <UserCog className="h-4 w-4 mr-1.5" /> Custom Roles {roles.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{roles.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="fields" data-testid="tab-custom-fields">
              <ListPlus className="h-4 w-4 mr-1.5" /> Custom Fields {fields.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{fields.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance-rules">
              <CalendarCheck2 className="h-4 w-4 mr-1.5" /> Attendance Rules
            </TabsTrigger>
            <TabsTrigger value="saf" data-testid="tab-self-assessments">
              <FileCheck2 className="h-4 w-4 mr-1.5" /> Self-Assessments
            </TabsTrigger>
            <TabsTrigger value="citizens" data-testid="tab-citizens">
              <Contact className="h-4 w-4 mr-1.5" /> Citizens
            </TabsTrigger>
            <TabsTrigger value="policies" data-testid="tab-policies">
              <FileSignature className="h-4 w-4 mr-1.5" /> Policies
            </TabsTrigger>
            <TabsTrigger value="watermark" data-testid="tab-watermark">
              <Stamp className="h-4 w-4 mr-1.5" /> Watermark
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              <BellRing className="h-4 w-4 mr-1.5" /> Messages
            </TabsTrigger>
          </TabsList>

          {/* ---------- ROLES TAB ---------- */}
          <TabsContent value="roles" className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Custom roles appear in the municipality's "Add User" role dropdown. Staff sign into the portal chosen by the role's template.
              </p>
              <Button onClick={() => { setEditRole(null); setRoleDialogOpen(true); }} data-testid="add-role-button">
                <Plus className="h-4 w-4 mr-2" /> Add Role
              </Button>
            </div>
            {rolesData === null ? <Spinner /> : roles.length === 0 ? (
              <EmptyState title="No custom roles yet"
                subtitle='Create roles like "SWM Inspector" or "Ward Officer" tailored to this municipality.'
                action={<Button onClick={() => { setEditRole(null); setRoleDialogOpen(true); }} data-testid="empty-add-role-button"><Plus className="h-4 w-4 mr-2" /> Add Role</Button>} />
            ) : (
              <Card className="shadow-sm">
                <CardContent className="p-0">
                  <Table data-testid="custom-roles-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Role</TableHead>
                        <TableHead>Portal</TableHead>
                        <TableHead className="hidden md:table-cell">Modules</TableHead>
                        <TableHead className="text-center">Staff</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {roles.map((r) => {
                        const meta = PORTAL_META[r.base_role] || {};
                        const PortalIcon = meta.icon || Monitor;
                        const granted = Object.entries(r.modules || {}).filter(([, v]) => v !== "none");
                        return (
                          <TableRow key={r.id} className="hover:bg-muted/60" data-testid={`role-row-${r.key}`}>
                            <TableCell>
                              <p className="font-medium text-sm">{r.name}</p>
                              {r.description && <p className="text-xs text-muted-foreground truncate max-w-[260px]">{r.description}</p>}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-1.5 text-xs">
                                <PortalIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                {meta.label} <Badge variant="outline" className="text-[10px]">{meta.device}</Badge>
                              </span>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <div className="flex flex-wrap gap-1 max-w-[320px]">
                                {granted.slice(0, 4).map(([k, v]) => (
                                  <Badge key={k} variant="outline" className={`text-[10px] ${LEVEL_BADGE[v] || ""}`}>{k}</Badge>
                                ))}
                                {granted.length > 4 && <Badge variant="secondary" className="text-[10px]">+{granted.length - 4} more</Badge>}
                                {granted.length === 0 && <span className="text-xs text-muted-foreground">Landing page only</span>}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center gap-1 text-sm" data-testid={`role-user-count-${r.key}`}>
                                <Users className="h-3.5 w-3.5 text-muted-foreground" /> {r.user_count || 0}
                              </span>
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit role"
                                onClick={() => { setEditRole(r); setRoleDialogOpen(true); }} data-testid={`role-edit-${r.key}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete role"
                                onClick={() => setDeleteRole(r)} data-testid={`role-delete-${r.key}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ---------- FIELDS TAB ---------- */}
          <TabsContent value="fields" className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Custom fields appear on this municipality's staff create & edit forms. Values are stored per staff member.
              </p>
              <Button onClick={() => { setEditField(null); setFieldDialogOpen(true); }} data-testid="add-field-button">
                <Plus className="h-4 w-4 mr-2" /> Add Field
              </Button>
            </div>
            {fieldsData === null ? <Spinner /> : fields.length === 0 ? (
              <EmptyState title="No custom fields yet"
                subtitle='Add fields like "Aadhaar Number" or "Ward Assignment ID" to the staff form.'
                action={<Button onClick={() => { setEditField(null); setFieldDialogOpen(true); }} data-testid="empty-add-field-button"><Plus className="h-4 w-4 mr-2" /> Add Field</Button>} />
            ) : (
              <Card className="shadow-sm">
                <CardContent className="p-0">
                  <Table data-testid="custom-fields-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Label</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="hidden md:table-cell">Options</TableHead>
                        <TableHead className="text-center">Required</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fields.map((f) => (
                        <TableRow key={f.id} className="hover:bg-muted/60" data-testid={`field-row-${f.key}`}>
                          <TableCell>
                            <p className="font-medium text-sm">{f.label}</p>
                            <p className="text-[11px] text-muted-foreground font-mono">{f.key}</p>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{FIELD_TYPE_LABELS[f.field_type] || f.field_type}</Badge></TableCell>
                          <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                            {f.field_type === "select" ? (f.options || []).join(", ") : "—"}
                          </TableCell>
                          <TableCell className="text-center">
                            {f.required
                              ? <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200" variant="outline">Required</Badge>
                              : <span className="text-xs text-muted-foreground">Optional</span>}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit field"
                              onClick={() => { setEditField(f); setFieldDialogOpen(true); }} data-testid={`field-edit-${f.key}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete field"
                              onClick={() => setDeleteField(f)} data-testid={`field-delete-${f.key}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ---------- ATTENDANCE RULES TAB (Phase 168) ---------- */}
          <TabsContent value="attendance" className="mt-4 space-y-3">
            <AttendanceRulesForm code={code} />
          </TabsContent>

          {/* ---------- SELF-ASSESSMENTS TAB (Phase 210) ---------- */}
          <TabsContent value="saf" className="mt-4 space-y-3" data-testid="tenantconfig-saf-tab">
            <SuperAdminSafPanel code={code} />
          </TabsContent>

          {/* ---------- CITIZENS TAB (Phase 215) ---------- */}
          <TabsContent value="citizens" className="mt-4 space-y-3" data-testid="tenantconfig-citizens-tab">
            <SuperAdminCitizensPanel key={code} code={code} />
          </TabsContent>

          {/* ---------- POLICIES TAB (Phase 218) ---------- */}
          <TabsContent value="policies" className="mt-4 space-y-3" data-testid="tenantconfig-policies-tab">
            <SuperAdminPoliciesPanel key={code} code={code} />
          </TabsContent>

          {/* ---------- WATERMARK TAB (Phase 224) ---------- */}
          <TabsContent value="watermark" className="mt-4 space-y-3" data-testid="tenantconfig-watermark-tab">
            <SuperAdminWatermarkPanel key={code} code={code} />
          </TabsContent>

          {/* ---------- MESSAGES TAB (Phase 227): citizen / staff switches + Automatic / Manual per message type ---------- */}
          <TabsContent value="messages" className="mt-4 space-y-3" data-testid="tenantconfig-messages-tab">
            <p className="text-sm text-muted-foreground">
              Decide for <span className="font-medium text-foreground">{munis.find((m) => m.code === code)?.name || code}</span> whether citizen and staff messages go out at all, and which message types the system sends automatically versus the Municipal Admin sends by hand from Notifications → Send Messages.
            </p>
            <SuperAdminMessagesPanel key={code} code={code} />
          </TabsContent>
        </Tabs>
      )}

      <RoleDialog code={code} open={roleDialogOpen} onOpenChange={setRoleDialogOpen}
        registry={rolesData?.registry} baseRoles={rolesData?.base_roles} role={editRole} onSaved={loadRoles} />
      <FieldDialog code={code} open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}
        field={editField} onSaved={loadFields} />

      {/* Delete role confirm */}
      <AlertDialog open={!!deleteRole} onOpenChange={(v) => !v && setDeleteRole(null)}>
        <AlertDialogContent data-testid="delete-role-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role "{deleteRole?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {(deleteRole?.user_count || 0) > 0
                ? `${deleteRole.user_count} staff member(s) currently hold this role — deletion will be blocked until they are reassigned or deactivated.`
                : "This role will be removed from the municipality's role dropdown. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-role-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteRole} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-role-confirm-button">Delete role</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete field confirm */}
      <AlertDialog open={!!deleteField} onOpenChange={(v) => !v && setDeleteField(null)}>
        <AlertDialogContent data-testid="delete-field-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete field "{deleteField?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The field will disappear from the staff form. Values already saved on existing staff remain stored but are no longer shown or editable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-field-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteField} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-field-confirm-button">Delete field</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
